// SSE client for GET /api/v1/scans/{scanId}/events
// UI_SDD.md Section 9

import type { ScanEvent, ScanState } from '../types/api'

const TERMINAL_STATES: ScanState[] = ['complete', 'partial', 'failed']

/**
 * Subscribe to live scan events via SSE.
 *
 * Returns a cleanup function — call it on component unmount.
 *
 * **No credential appears in this URL** (DEC-141). EventSource cannot set
 * headers, which is why the JWT used to travel as `?token=`; that wrote a
 * working credential into every URL, and Caddy, the reverse proxy this
 * deployment runs behind, logs query strings by default. A same-origin
 * EventSource sends the `tdia_session` cookie on its own, so the parameter was
 * removed rather than merely discouraged, and the backend no longer accepts it.
 */
export function subscribeScanEvents(
  scanId: string,
  onEvent: (event: ScanEvent) => void,
  onError: (error: Event) => void,
  onComplete: () => void,
  startIndex: number = 0,
): () => void {
  const params = new URLSearchParams({ start: String(startIndex) })

  const url = `/api/v1/scans/${scanId}/events?${params.toString()}`
  // `withCredentials` is what sends the session cookie on the SSE connection.
  const source = new EventSource(url, { withCredentials: true })

  source.onmessage = (e: MessageEvent) => {
    try {
      const event: ScanEvent = JSON.parse(e.data as string)
      onEvent(event)

      if (
        event.event_type === 'scan_state_changed' &&
        TERMINAL_STATES.includes(event.state)
      ) {
        source.close()
        onComplete()
      }
    } catch (err) {
      console.error('[SSE] Failed to parse event:', err, e.data)
    }
  }

  source.onerror = (e: Event) => {
    onError(e)
    // Browser reconnects automatically after ~3s; we surface the error
    // to the UI but don't close the source.
  }

  return () => source.close()
}
